import React from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { BiPencil, BiMapPin } from 'react-icons/bi';
import { toast } from 'react-toastify';

import ManualOrderEntryDialog from '../components/ManualOrderEntryDialog';
import EditOrderDialog from '../components/EditOrderDialog';
import { capabilityGate } from '@hopdrive/feature-portal-products';
import { useProductEntry } from '@features/products/useProductEntry';
import DeliveryRunPanel, { type FulfillmentMode } from '../components/DeliveryRunPanel';
import ThirdPartyRunPanel from '../components/ThirdPartyRunPanel';
import InstantQuoteForm from '../components/InstantQuoteForm';
import { useThirdPartyBooking } from '../hooks/useThirdPartyBooking';
import { normAddr, buildStopForDestination } from '../utils/stopBuilder';
import { MovePlannerLocationSelect } from '@hopdrive/feature-move-planner';
import { useCustomerConfig, useRooftop, useUser } from '@store';
import { useGetAllLocationsQuery } from '../../locations/queries/getAllLocations.query.generated';
import { useAssignPartsOrdersMutation } from '../queries/assignPartsOrders.mutation.generated';
import { useGetLocationsByIdsLazyQuery } from '../queries/getLocationsByIds.query.generated';
import { useGetPartsPlannerDataQuery } from '../queries/getPartsPlannerData.query.generated';
import { useGetPartsPodWorkflowsetsQuery } from '../queries/getPartsPodWorkflowsets.query.generated';
import { useGetRouteTemplatesQuery } from '../queries/getRouteTemplates.query.generated';
import { useInsertPartsRunMutation } from '../queries/insertPartsRun.mutation.generated';
import { useInsertPartsStopMutation } from '../queries/insertPartsStop.mutation.generated';
import { useInsertRouteTemplateMutation } from '../queries/routeTemplateMutations.generated';
import { Locations } from '@gql/base-types';
import { useShellTabActions } from '@features/products/shell/shellTabActions';
import ViewModeSwitcher from '@components/Utils/ViewModeSwitcher';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';

// A draft-run stop is a DESTINATION (a location/address) holding 1..N orders.
// Orders group by normalized delivery address (normAddr, shared via utils/stopBuilder);
// template shelves carry a location_id.

// Human-readable billing terms (stored snake_case on delivery_orders).
const BILLING_LABELS: Record<string, string> = {
  on_account: 'On Account',
  cod: 'COD',
  net_30: 'Net 30',
  urgent: 'Urgent',
};
const formatBillingTerm = (term?: string | null): string =>
  term
    ? BILLING_LABELS[term] ?? term.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : 'On Account';
const runStops = (run: any): any[] => run?.stops || [];
const runOrders = (run: any): any[] => runStops(run).flatMap((s: any) => s.delivery_orders || []);
const renumber = (stops: any[]): any[] => stops.map((s, i) => ({ ...s, stop_order: i + 1 }));

/**
 * The Pending Orders tab of the Parts Delivery shell: the orders waiting on one side, the run being
 * built on the other. The layout owns the frame, the tab strip, and the header: Add Order is the
 * product's creation method there, and this tab contributes its refresh as the header's sync.
 */
const PendingOrdersTab: React.FC = () => {
  const rooftop = useRooftop();
  const user = useUser();
  const customerConfig = useCustomerConfig();
  const customerId = rooftop?.id || 0;
  const userEmail = user?.profile?.email || 'unknown';
  // Building/dispatching a run and editing pending orders are write actions gated on the Parts
  // Delivery product's capabilities; a read_only product shows pending orders for viewing only.
  const pdEntry = useProductEntry('parts-delivery');
  const canManagePd = capabilityGate(pdEntry, 'manageExisting').kind === 'render';
  const canCreatePd = capabilityGate(pdEntry, 'createNew').kind === 'render';
  const thirdPartyEnabled = customerConfig?.parts_delivery?.third_party === true;

  const { data, loading, refetch } = useGetPartsPlannerDataQuery({
    variables: { customerId, today: dayjs().startOf('day').toISOString() },
    skip: !customerId,
  });

  const { data: workflowsetData } = useGetPartsPodWorkflowsetsQuery({
    variables: { customerId },
    skip: !customerId,
  });
  const workflowsets = workflowsetData?.workflowsets || [];
  const defaultWorkflowsetId = workflowsets.length === 1 ? workflowsets[0].id : null;

  const { data: templateData, refetch: refetchTemplates } = useGetRouteTemplatesQuery({
    variables: { customerId },
    skip: !customerId,
  });
  const templates = templateData?.route_templates || [];

  const [resolveLocations] = useGetLocationsByIdsLazyQuery();
  const [createRun] = useInsertPartsRunMutation();
  const [createStop] = useInsertPartsStopMutation();
  const [assignOrders] = useAssignPartsOrdersMutation();
  const [insertTemplate] = useInsertRouteTemplateMutation();

  const [pendingOrders, setPendingOrders] = React.useState<any[]>([]);
  const [draftRun, setDraftRun] = React.useState<any | null>(null);
  const [selectedOrders, setSelectedOrders] = React.useState<Set<number>>(new Set());
  const [draggedOrder, setDraggedOrder] = React.useState<any | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  const [manualEntryOpen, setManualEntryOpen] = React.useState(false);
  const [quickAddDest, setQuickAddDest] = React.useState<any | null>(null);
  const [editingOrder, setEditingOrder] = React.useState<any | null>(null);
  const [checkInOpen, setCheckInOpen] = React.useState(false);
  const [checkInLoc, setCheckInLoc] = React.useState<Locations | null>(null);
  const [templateNameOpen, setTemplateNameOpen] = React.useState(false);
  const [templateName, setTemplateName] = React.useState('');
  const [savingTemplate, setSavingTemplate] = React.useState(false);

  // Fulfillment mode: own drivers vs a 3rd-party courier (flag-gated).
  const [fulfillMode, setFulfillMode] = React.useState<FulfillmentMode>('own');
  const {
    book: bookThirdParty,
    booking: thirdPartyBooking,
    pickup: thirdPartyPickup,
    pickupProblem: thirdPartyPickupProblem,
  } = useThirdPartyBooking(() => {
    setDraftRun(null);
    setSelectedOrders(new Set());
    refetch();
  });

  // An app update waits while the draft run has stops, or a dispatch, template save, or
  // courier booking is running. Dispatching, booking, or removing the last stop clears it.
  const holdsUnsavedWork = (draftRun?.stops?.length ?? 0) > 0 || submitting || savingTemplate || thirdPartyBooking;
  useReloadGuard(holdsUnsavedWork, 'Run builder');

  // Saved/favorite locations for the check-in-stop picker (favorites first).
  const { data: allLocationsData, refetch: refetchAllLocations } = useGetAllLocationsQuery({
    variables: { customerId },
    skip: !customerId,
    fetchPolicy: 'cache-and-network',
  });
  const savedLocations = (allLocationsData?.locations ?? []) as (Locations | null)[];
  const [viewMode, setViewMode] = React.useState<'table' | 'tiles'>(() => {
    return (localStorage.getItem('parts-delivery-view-mode') as 'table' | 'tiles') || 'table';
  });

  // Refs so the [data] effect reads the latest draft/pending without re-running on them.
  const draftRunRef = React.useRef<any | null>(null);
  const pendingRef = React.useRef<any[]>([]);
  React.useEffect(() => { draftRunRef.current = draftRun; }, [draftRun]);
  React.useEffect(() => { pendingRef.current = pendingOrders; }, [pendingOrders]);

  const drivers = data?.drivers || [];
  const vehicles = data?.vehicles || [];

  // Seed/refresh the pending pool from the server whenever data changes. Orders
  // already in the draft run stay out of the pool.
  React.useEffect(() => {
    if (!data) return;
    const inRun = new Set(runOrders(draftRunRef.current).map((o: any) => o.id));
    setPendingOrders((data.unassigned_orders || []).filter((o: any) => !inRun.has(o.id)));
  }, [data]);

  const makeBaseRun = () => ({
    id: 'draft-run',
    isDraft: true,
    driver_id: null,
    vehicle_id: null,
    profile_id: defaultWorkflowsetId,
    template_id: null as number | null,
    departure_time: dayjs().add(1, 'hour').startOf('hour').toISOString(),
    status: 'draft',
    stops: [] as any[],
  });

  const makeStopFromOrder = (o: any) => ({
    key: normAddr(o.delivery_address),
    id: `dstop-${normAddr(o.delivery_address) || o.id}`,
    location_id: o.location_id ?? null,
    address: o.delivery_address || '',
    recipient_business: o.recipient_business || '',
    recipient_name: o.recipient_name || '',
    recipient_phone: o.recipient_phone || '',
    delivery_lat: o.delivery_lat ?? null,
    delivery_lng: o.delivery_lng ?? null,
    fromTemplate: false,
    delivery_orders: [o],
  });

  // The tab's contribution to the header: its refresh, covering the planner data and the template
  // picker (New Template in the header can land while this tab is showing).
  const refetchAll = React.useCallback(async () => { await Promise.all([refetch(), refetchTemplates()]); }, [refetch, refetchTemplates]);
  const tabActions = React.useMemo(() => ({ sync: refetchAll }), [refetchAll]);
  useShellTabActions(tabActions);

  const closeManualEntry = () => { setManualEntryOpen(false); setQuickAddDest(null); };
  const handleManualEntrySuccess = () => refetch();

  const handleEditOrder = (e: React.MouseEvent, order: any) => {
    e.stopPropagation();
    setEditingOrder(order);
  };

  const selectViewMode = (newMode: 'table' | 'tiles') => {
    localStorage.setItem('parts-delivery-view-mode', newMode);
    setViewMode(newMode);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOrders(event.target.checked ? new Set(pendingOrders.map(o => o.id)) : new Set());
  };

  const handleSelectOrder = (orderId: number) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      next.has(orderId) ? next.delete(orderId) : next.add(orderId);
      return next;
    });
  };

  // Add orders to the run, grouping each into its destination (by address).
  const addOrdersToRun = (orders: any[]) => {
    if (!orders.length) return;
    setDraftRun((prev: any) => {
      const base = prev || makeBaseRun();
      const existingIds = new Set(runOrders(base).map((o: any) => o.id));
      const fresh = orders.filter(o => !existingIds.has(o.id));
      if (!fresh.length) return base;
      const stops = base.stops.map((s: any) => ({ ...s, delivery_orders: [...s.delivery_orders] }));
      for (const o of fresh) {
        const key = normAddr(o.delivery_address);
        const idx = stops.findIndex((s: any) => s.key === key);
        if (idx >= 0) stops[idx].delivery_orders.push(o);
        else stops.push(makeStopFromOrder(o));
      }
      return { ...base, stops: renumber(stops) };
    });
    const movedIds = new Set(orders.map(o => o.id));
    setPendingOrders(prev => prev.filter(o => !movedIds.has(o.id)));
    setSelectedOrders(new Set());
  };

  const seedRunFromSelection = () => {
    addOrdersToRun(pendingOrders.filter(o => selectedOrders.has(o.id)));
  };

  // Add an order-less "check-in" stop (driver checks for a core/return to grab).
  const addCheckInStop = (loc?: Locations | null) => {
    if (!loc) return;
    const key = normAddr(loc.address || '') || `checkin-${loc.id ?? 'x'}`;
    setDraftRun((prev: any) => {
      const base = prev || makeBaseRun();
      if (base.stops.some((s: any) => s.key === key)) return base;
      const stop = {
        key,
        id: `dstop-${key}`,
        location_id: loc.id ?? null,
        address: loc.address || '',
        recipient_business: loc.name || loc.nickname || '',
        recipient_name: '',
        recipient_phone: (loc as any).phone || '',
        delivery_lat: loc.latitude != null ? Number(loc.latitude) : null,
        delivery_lng: loc.longitude != null ? Number(loc.longitude) : null,
        fromTemplate: false,
        checkIn: true,
        delivery_orders: [] as any[],
      };
      return { ...base, stops: renumber([...base.stops, stop]) };
    });
    setCheckInLoc(null);
    setCheckInOpen(false);
  };

  // Return one order from the run to the pending pool. Drop the destination if it
  // empties — unless it's a template shelf (those persist as planned stops).
  const removeOrderFromRun = (orderId: number) => {
    let removed: any = null;
    setDraftRun((prev: any) => {
      if (!prev) return prev;
      const stops = prev.stops
        .map((s: any) => {
          const kept = s.delivery_orders.filter((o: any) => {
            if (o.id === orderId) removed = o;
            return o.id !== orderId;
          });
          return { ...s, delivery_orders: kept };
        })
        .filter((s: any) => s.delivery_orders.length > 0 || s.fromTemplate || s.checkIn);
      if (stops.length === 0) return null;
      return { ...prev, stops: renumber(stops) };
    });
    if (removed) setPendingOrders(prev => (prev.some(o => o.id === orderId) ? prev : [...prev, removed]));
  };

  // Remove an entire destination; its orders return to the pending pool.
  const removeDestination = (key: string) => {
    let returned: any[] = [];
    setDraftRun((prev: any) => {
      if (!prev) return prev;
      const stops = prev.stops.filter((s: any) => {
        if (s.key === key) { returned = s.delivery_orders || []; return false; }
        return true;
      });
      if (stops.length === 0) return null;
      return { ...prev, stops: renumber(stops) };
    });
    if (returned.length) {
      setPendingOrders(prev => {
        const have = new Set(prev.map(o => o.id));
        return [...prev, ...returned.filter(o => !have.has(o.id))];
      });
    }
  };

  // Reorder destinations within the run and renumber.
  const reorderRunStops = (fromIndex: number, toIndex: number) => {
    setDraftRun((prev: any) => {
      if (!prev) return prev;
      const stops = [...prev.stops];
      if (fromIndex < 0 || fromIndex >= stops.length || toIndex < 0 || toIndex >= stops.length || fromIndex === toIndex) {
        return prev;
      }
      const [moved] = stops.splice(fromIndex, 1);
      stops.splice(toIndex, 0, moved);
      return { ...prev, stops: renumber(stops) };
    });
  };

  const handleUpdateRun = (field: string, value: any) => {
    let storedValue: any = value === '' ? null : value;
    if (field === 'departure_time' && typeof value === 'string' && value) {
      // datetime-local ("YYYY-MM-DDTHH:mm") → ISO; legacy time-only ("HH:mm")
      // falls back to today's date.
      storedValue = value.includes('T')
        ? dayjs(value).toISOString()
        : dayjs(`${dayjs().format('YYYY-MM-DD')}T${value}`).toISOString();
    }
    setDraftRun((prev: any) => (prev ? { ...prev, [field]: storedValue } : prev));
  };

  // ── Drag and drop (pending → run) ──
  const handleDragStart = (order: any) => setDraggedOrder(order);
  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDropOnRun = () => {
    if (draggedOrder) addOrdersToRun([draggedOrder]);
    setDraggedOrder(null);
  };

  // ── Route templates ──
  // Load a template's destinations as shelves, then pull matching pending orders in.
  const loadTemplate = async (templateId: number) => {
    const tpl = templates.find((t: any) => t.id === templateId);
    if (!tpl) return;
    const raw: any[] = Array.isArray(tpl.stops) ? tpl.stops : [];
    const ordered = [...raw].sort((a, b) => (a.stop_order ?? 0) - (b.stop_order ?? 0));
    const ids = ordered.map(s => s.location_id).filter(Boolean);
    let byId = new Map<number, any>();
    if (ids.length) {
      const res = await resolveLocations({ variables: { ids } });
      byId = new Map((res.data?.locations ?? []).map((l: any) => [l.id, l]));
    }
    const shelves = ordered.map(s => {
      const loc = byId.get(s.location_id);
      const address = loc?.address || '';
      return {
        key: normAddr(address),
        id: `tpl-${s.location_id}`,
        location_id: s.location_id,
        address,
        recipient_business: loc?.name || loc?.nickname || '',
        // Contact person isn't stored on locations — it's denormalized onto the
        // template stop. Phone prefers the saved override, else the location phone.
        recipient_name: s.recipient_name || '',
        recipient_phone: s.recipient_phone || loc?.phone || '',
        delivery_lat: loc?.latitude != null ? Number(loc.latitude) : null,
        delivery_lng: loc?.longitude != null ? Number(loc.longitude) : null,
        fromTemplate: true,
        delivery_orders: [] as any[],
      };
    });

    const base = draftRunRef.current || makeBaseRun();
    const stops = base.stops.map((s: any) => ({ ...s, delivery_orders: [...s.delivery_orders] }));
    for (const sh of shelves) {
      if (!stops.some((s: any) => s.key === sh.key)) stops.push(sh);
    }
    // Pull currently-pending orders whose address matches a shelf.
    const keyset = new Set(stops.map((s: any) => s.key));
    const pend = pendingRef.current || [];
    const pulled = pend.filter(o => keyset.has(normAddr(o.delivery_address)));
    for (const o of pulled) {
      const idx = stops.findIndex((s: any) => s.key === normAddr(o.delivery_address));
      if (idx >= 0 && !stops[idx].delivery_orders.some((x: any) => x.id === o.id)) {
        stops[idx].delivery_orders.push(o);
      }
    }
    const pulledIds = new Set(pulled.map(o => o.id));
    setDraftRun({ ...base, template_id: templateId, stops: renumber(stops) });
    setPendingOrders((pendingRef.current || []).filter(o => !pulledIds.has(o.id)));
    toast.success(`Loaded "${tpl.name}" — ${shelves.length} destination${shelves.length !== 1 ? 's' : ''}`, { toastId: 'parts-delivery-template-load' });
  };

  // Open the name dialog (a run needs at least one destination to save).
  const saveCurrentRunAsTemplate = () => {
    if (!(draftRunRef.current?.stops || []).length) {
      toast.error('Add at least one destination before saving a template.', { toastId: 'parts-delivery-template-save' });
      return;
    }
    setTemplateName('');
    setTemplateNameOpen(true);
  };

  const confirmSaveTemplate = async () => {
    const name = templateName.trim();
    if (!name) return;
    const base = draftRunRef.current;
    const allStops = base?.stops || [];
    // Templates reference locations by id. Order-built stops carry no location_id
    // (delivery_orders don't store one), but the address has a saved locations row
    // (the picker created it) — resolve the id by matching address.
    const res = await refetchAllLocations();
    const locs = (res.data?.locations ?? savedLocations).filter(Boolean) as Locations[];
    const idByAddr = new Map(locs.map(l => [normAddr(l.address || ''), l.id]));
    const resolved = allStops.map((s: any) => ({
      ...s,
      location_id: s.location_id ?? idByAddr.get(normAddr(s.address || '')) ?? null,
    }));
    const dests = resolved.filter((s: any) => s.location_id);
    if (!dests.length) {
      toast.error('These destinations aren’t saved locations yet, so the route can’t be saved as a template.', { toastId: 'parts-delivery-template-save' });
      return;
    }
    const skipped = allStops.length - dests.length;
    setSavingTemplate(true);
    try {
      await insertTemplate({
        variables: {
          template: {
            customer_id: customerId,
            product_type: 'parts_delivery',
            name,
            stops: dests.map((s: any, i: number) => ({
              location_id: s.location_id,
              stop_order: i + 1,
              // Persist the contact so reloading the template prefills it (locations
              // store no contact-person field).
              recipient_name: s.recipient_name || null,
              recipient_phone: s.recipient_phone || null,
            })),
            created_by: userEmail,
          },
        },
      });
      refetchTemplates();
      toast.success(
        `Template "${name}" saved` + (skipped > 0 ? ` (${skipped} non-location stop${skipped !== 1 ? 's' : ''} skipped)` : ''),
      );
      setTemplateNameOpen(false);
    } catch (error: any) {
      console.error('Failed to save template:', error);
      toast.error(`Failed to save template: ${error?.message || 'Unknown error'}`, { toastId: 'parts-delivery-template-save' });
    } finally {
      setSavingTemplate(false);
    }
  };

  // Per-destination quick-add: open the manual-entry dialog pre-filled with the
  // destination; created orders are placed straight onto its shelf.
  const openQuickAdd = (dest: any) => {
    setQuickAddDest(dest);
    setManualEntryOpen(true);
  };

  const handleDispatch = async () => {
    const base = draftRunRef.current;
    // Keep stops that carry orders AND intentional order-less "check-in" stops
    // (template shelves or ad-hoc check-in stops); drop accidental empty shelves.
    const dests = (base?.stops || []).filter(
      (s: any) => (s.delivery_orders || []).length > 0 || s.fromTemplate || s.checkIn,
    );
    if (dests.length === 0) {
      toast.warning('Add at least one stop before dispatching.', { toastId: 'parts-delivery-dispatch' });
      return;
    }
    setSubmitting(true);
    try {
      const runRes = await createRun({
        variables: {
          run: {
            customer_id: customerId,
            product_type: 'parts_delivery',
            status: 'scheduled',
            departure_time: base.departure_time || null,
            driver_id: null,
            vehicle_id: base.vehicle_id || null,
            // vehicle_mode drives the driver app's select-vehicle screen (same
            // contract as Mobile Service): 'manual' = vehicle preassigned here,
            // 'driver_picks' = driver chooses when claiming the run. Without it
            // the app prompts even when vehicle_id is set.
            config: {
              vehicle_mode: base.vehicle_id ? 'manual' : 'driver_picks',
            },
            profile_id: base.profile_id || null,
            template_id: base.template_id || null,
            created_by: userEmail,
            updated_by: userEmail,
          },
        },
      });
      const newRunId = runRes.data?.insert_mobility_runs_one?.id;
      if (!newRunId) throw new Error('Failed to create run');

      let stopOrder = 0;
      for (const dest of dests) {
        stopOrder += 1;
        const stopRes = await createStop({ variables: { stop: buildStopForDestination(newRunId, dest, stopOrder) } });
        const stopId = stopRes.data?.insert_mobility_stops_one?.id;
        if (stopId) {
          const orderIds = (dest.delivery_orders || []).map((o: any) => o.id);
          if (orderIds.length) await assignOrders({ variables: { orderIds, runId: newRunId, stopId } });
        }
      }

      try {
        const token = await user?.getToken();
        if (token) {
          const dispatchRes = await axios({
            method: 'POST',
            url: `${window.location.origin}/.netlify/functions/dispatchPartsDeliveryRun`,
            data: { runId: newRunId, targetDriverId: base.driver_id || null },
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
          if (!dispatchRes.data?.success) {
            console.warn('Parts dispatch returned non-success:', dispatchRes.data);
            toast.warning('Run created but dispatch failed. You can re-dispatch from the run.', { toastId: 'parts-delivery-dispatch' });
          } else {
            const orderTotal = dests.reduce((n: number, s: any) => n + (s.delivery_orders || []).length, 0);
            toast.success(`Run dispatched: ${dests.length} stop${dests.length !== 1 ? 's' : ''}, ${orderTotal} order${orderTotal !== 1 ? 's' : ''}`, { toastId: 'parts-delivery-dispatch' });
          }
        }
      } catch (dispatchErr: any) {
        console.error('Failed to dispatch parts run:', dispatchErr);
        toast.warning('Run created but dispatch failed. You can re-dispatch from the run.', { toastId: 'parts-delivery-dispatch' });
      }

      setDraftRun(null);
      setSelectedOrders(new Set());
      refetch();
    } catch (error: any) {
      console.error('Failed to create run:', error);
      toast.error(`Failed to create run: ${error?.message || 'Unknown error'}`, { toastId: 'parts-delivery-dispatch' });
    } finally {
      setSubmitting(false);
    }
  };

  const allSelected = pendingOrders.length > 0 && selectedOrders.size === pendingOrders.length;
  const someSelected = selectedOrders.size > 0 && selectedOrders.size < pendingOrders.length;

  // Check-in stop edits the draft run, so it stays with the tab.
  const checkInButton = (sx: Record<string, unknown> = {}) => (
    <Button
      variant='outlined'
      size='small'
      startIcon={<BiMapPin />}
      onClick={() => setCheckInOpen(true)}
      sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', ...sx }}
    >
      Check-in stop
    </Button>
  );

  // The tab's controls row: the view toggle and Check-in stop; Add Order and refresh live in the
  // shell header.
  const controls = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 2 }}>
      <ViewModeSwitcher size='small' value={viewMode} onChange={selectViewMode} options={['table', 'tiles']} />
      <Box sx={{ flex: 1 }} />
      {checkInButton()}
    </Box>
  );

  const content = (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        {/* Left: pending orders */}
        <Grid size={{ xs: 12, md: 7 }}>
          {selectedOrders.size > 0 && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant='body2' color='text.secondary'>
                {selectedOrders.size} order{selectedOrders.size !== 1 ? 's' : ''} selected
              </Typography>
              <Button
                variant='contained'
                size='small'
                onClick={seedRunFromSelection}
                data-testid='pd-create-run'
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {draftRun ? 'Add to run' : 'Create Run'}
              </Button>
            </Box>
          )}

          <Card>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant='h6' sx={{ fontWeight: 700 }}>
                Pending Delivery Orders
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Select orders to create a delivery run, or drag them onto the run.
              </Typography>
            </Box>

            {loading ? (
              <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : pendingOrders.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant='body2' color='text.secondary'>
                  {draftRun ? 'All pending orders are on the run.' : 'No pending orders. Click "Add Order" to create one.'}
                </Typography>
              </Box>
            ) : viewMode === 'table' ? (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding='checkbox'>
                      <Checkbox checked={allSelected} indeterminate={someSelected} onChange={handleSelectAll} />
                    </TableCell>
                    {['Order #', 'Recipient', 'Pkgs', 'Priority', 'Notes', 'Billing'].map((h, i) => (
                      <TableCell
                        key={h}
                        align={i === 2 ? 'center' : 'left'}
                        sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: 11 }}
                      >
                        {h}
                      </TableCell>
                    ))}
                    <TableCell
                      align='right'
                      sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: 11 }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingOrders.map((order: any) => (
                    <TableRow
                      key={order.id}
                      hover
                      draggable
                      onDragStart={() => handleDragStart(order)}
                      selected={selectedOrders.has(order.id)}
                      onClick={() => handleSelectOrder(order.id)}
                      data-testid='pd-order-row'
                      data-order-number={order.order_number}
                      sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
                    >
                      <TableCell padding='checkbox'>
                        <Checkbox checked={selectedOrders.has(order.id)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2' sx={{ color: 'primary.main', fontWeight: 600 }}>
                          {order.order_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2' sx={{ fontWeight: 600 }}>
                          {order.recipient_business}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {order.recipient_name}
                        </Typography>
                      </TableCell>
                      <TableCell align='center'>
                        <Typography variant='body2' sx={{ fontWeight: 600 }}>
                          {order.quantity || 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {order.priority === 'rush' ? (
                          <Typography variant='body2' sx={{ color: 'error.main', fontWeight: 600 }}>
                            Urgent
                          </Typography>
                        ) : (
                          <Typography variant='body2' color='text.secondary'>
                            Normal
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2' color='text.secondary'>
                          {order.item_description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant='body2'
                          sx={{ color: order.billing_term === 'cod' ? 'warning.main' : 'info.main', fontWeight: 600 }}
                        >
                          {formatBillingTerm(order.billing_term)}
                        </Typography>
                      </TableCell>
                      <TableCell align='right'>
                        {canManagePd ? (
                          <Tooltip title='Edit order'>
                            <IconButton size='small' onClick={e => handleEditOrder(e, order)}>
                              <BiPencil />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
                {pendingOrders.map((order: any) => (
                  <Card
                    key={order.id}
                    draggable
                    onDragStart={() => handleDragStart(order)}
                    sx={{
                      p: 2,
                      cursor: 'grab',
                      '&:active': { cursor: 'grabbing' },
                      border: '2px solid',
                      borderColor: selectedOrders.has(order.id) ? 'primary.main' : 'divider',
                      backgroundColor: selectedOrders.has(order.id) ? 'action.selected' : 'background.paper',
                      '&:hover': { borderColor: 'primary.light', backgroundColor: 'action.hover' },
                    }}
                    onClick={() => handleSelectOrder(order.id)}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant='body1' sx={{ color: 'primary.main', fontWeight: 600, mb: 0.5 }}>
                          {order.order_number}
                        </Typography>
                        <Typography variant='body2' sx={{ fontWeight: 600 }}>
                          {order.recipient_business}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {order.recipient_name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {canManagePd ? (
                          <Tooltip title='Edit order'>
                            <IconButton size='small' onClick={e => handleEditOrder(e, order)}>
                              <BiPencil />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        <Checkbox checked={selectedOrders.has(order.id)} onClick={e => e.stopPropagation()} />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                      <Chip label={`${order.quantity || 1} pkg${order.quantity !== 1 ? 's' : ''}`} size='small' color='default' />
                      <Chip
                        label={order.priority === 'rush' ? 'Urgent' : 'Normal'}
                        size='small'
                        color={order.priority === 'rush' ? 'error' : 'default'}
                      />
                      <Chip label={formatBillingTerm(order.billing_term)} size='small' color={order.billing_term === 'cod' ? 'warning' : 'info'} />
                    </Box>
                    {order.item_description && (
                      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1.5 }}>
                        {order.item_description}
                      </Typography>
                    )}
                  </Card>
                ))}
              </Box>
            )}
          </Card>
        </Grid>

        {/* Right: the run being built */}
        <Grid size={{ xs: 12, md: 5 }}>
          <DeliveryRunPanel
            run={draftRun}
            drivers={drivers}
            vehicles={vehicles}
            workflowsets={workflowsets}
            templates={templates}
            submitting={submitting}
            onUpdateRun={handleUpdateRun}
            onRemoveOrder={removeOrderFromRun}
            onRemoveDestination={removeDestination}
            onReorder={reorderRunStops}
            onQuickAdd={openQuickAdd}
            onLoadTemplate={loadTemplate}
            onSaveAsTemplate={saveCurrentRunAsTemplate}
            onDispatch={handleDispatch}
            writeAllowed={canManagePd}
            createAllowed={canCreatePd}
            onDragOver={handleDragOver}
            onDrop={handleDropOnRun}
            thirdPartyEnabled={thirdPartyEnabled}
            mode={fulfillMode}
            onModeChange={setFulfillMode}
            thirdPartyContent={
              <ThirdPartyRunPanel
                run={draftRun}
                pickup={thirdPartyPickup}
                pickupProblem={thirdPartyPickupProblem}
                booking={thirdPartyBooking}
                onBook={(quote, opts) => bookThirdParty(draftRunRef.current, quote, opts)}
                bookAllowed={canManagePd}
                instantQuoteSlot={
                  canManagePd ? <InstantQuoteForm
                    customerId={customerId}
                    userEmail={userEmail}
                    pickup={thirdPartyPickup}
                    pickupProblem={thirdPartyPickupProblem}
                    booking={thirdPartyBooking}
                    savedLocations={savedLocations}
                    refetchLocations={() => refetchAllLocations()}
                    onBook={(syntheticRun, quote, opts) => bookThirdParty(syntheticRun, quote, opts)}
                  /> : undefined
                }
              />
            }
          />
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <>
      {controls}
      {content}

      {/* The dialogs sit beside the content so an open dialog survives a re-render of the panes. The
          header's Add Order opens the product-level dialog; this instance serves the run builder's
          per-destination quick-add. */}
      <ManualOrderEntryDialog
        open={manualEntryOpen}
        onClose={closeManualEntry}
        onSuccess={handleManualEntrySuccess}
        prefill={
          quickAddDest
            ? {
                recipient_business: quickAddDest.recipient_business,
                recipient_name: quickAddDest.recipient_name,
                recipient_phone: quickAddDest.recipient_phone,
                delivery_address: quickAddDest.address,
                delivery_lat: quickAddDest.delivery_lat,
                delivery_lng: quickAddDest.delivery_lng,
              }
            : null
        }
        onCreated={quickAddDest ? (rows: any[]) => addOrdersToRun(rows) : undefined}
      />

      <EditOrderDialog
        open={!!editingOrder}
        order={editingOrder}
        onClose={() => setEditingOrder(null)}
        onSuccess={() => refetch()}
      />

      <Dialog open={checkInOpen} onClose={() => setCheckInOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add a check-in stop</DialogTitle>
        <DialogContent dividers>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            A stop with no delivery — the driver checks for a core, warranty swap, or return to bring back.
          </Typography>
          <MovePlannerLocationSelect
            rooftopId={customerId}
            label='Location'
            placeholder='Search saved locations or an address…'
            helperText='Favorites appear first — picking a location adds the stop'
            location={checkInLoc}
            locations={savedLocations}
            onLocationChange={addCheckInStop}
            onLocationFavorite={() => { refetchAllLocations(); }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant='outlined'
            onClick={() => setCheckInOpen(false)}
            sx={{ textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={templateNameOpen} onClose={() => !savingTemplate && setTemplateNameOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Save route template</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth size='small'
            label='Template name'
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
            placeholder='Tuesday Route'
            onKeyDown={e => { if (e.key === 'Enter' && templateName.trim() && !savingTemplate) confirmSaveTemplate(); }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant='outlined'
            onClick={() => setTemplateNameOpen(false)}
            disabled={savingTemplate}
            sx={{ textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={confirmSaveTemplate}
            disabled={!templateName.trim() || savingTemplate}
            startIcon={savingTemplate ? <CircularProgress size={16} /> : null}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {savingTemplate ? 'Saving…' : 'Save template'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PendingOrdersTab;
